<template>
  <div class="payouts-container">
    <h1 class="title-screen">{{ adminText('organizationAll.payouts_e0e7ea13', 'Payouts') }}</h1>
    <p class="helper-text">
      {{ adminText('organizationAll.organizer_payouts_overview_re_download_existi_80446783', 'Organizer payouts overview. Re-download existing payout bundles without creating new invoices.') }}
    </p>

    <spinner v-if="loading" />
    <div v-else>
      <p v-if="error" class="error-message">{{ organizationText(error) }}</p>
      <p v-else-if="!payouts.length" class="empty-message">
        {{ adminText('organizationAll.no_payouts_found_for_this_organization_920285ac', 'No payouts found for this organization.') }}
      </p>
      <div v-else class="table-wrapper">
        <table class="payouts-table">
          <thead>
            <tr>
              <th>{{ adminText('organizationAll.event_ad8919ac', 'Event') }}</th>
              <th>{{ adminText('organizationAll.period_170a28a9', 'Period') }}</th>
              <th>{{ adminText('organizationAll.status_bae7d5be', 'Status') }}</th>
              <th>{{ adminText('organizationAll.invoice_51d86044', 'Invoice #') }}</th>
              <th>{{ adminText('organizationAll.gross_9580a617', 'Gross') }}</th>
              <th>{{ adminText('organizationAll.issued_deb5d5e2', 'Issued') }}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-for="payout in payouts" :key="payout.id">
              <td>
                <div class="cell-title">{{ getEventTitle(payout) }}</div>
                <div class="cell-subtitle">{{ adminText('organizationAll.event_id_422e323a', 'Event ID:') }} {{ payout.eventId }}</div>
                <div v-if="getTemplateId(payout)" class="cell-subtitle">
                  {{ adminText('organizationAll.template_id_b25bb852', 'Template ID:') }} {{ getTemplateId(payout) }}
                </div>
              </td>
              <td>
                <div class="cell-title">
                  {{ formatPeriod(payout.periodFrom, payout.periodTo, payout.periodLabel) }}
                </div>
              </td>
              <td>
                <span class="status-pill" :class="payout.status">{{ organizationText(payout.status) }}</span>
              </td>
              <td>{{ payout.minimaxInvoiceNumber || '—' }}</td>
              <td>{{ formatCurrency(payout.grossAmount || payout.netAmount) }}</td>
              <td>{{ formatDate(payout.issuedAt || payout.createdAt) }}</td>
              <td>
                <main-button
                  small
                  flat
                  :loading="downloadingId === payout.id"
                  :disabled="downloadingId === payout.id"
                  @click.native="downloadPayout(payout)"
                >
                  {{ adminText('organizationAll.download_a479c9c3', 'Download') }}
                </main-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { downloadEventPayoutDocuments, downloadEventTemplatePayoutDocuments } from '@/http'
import { GET_ORGANIZER_PAYOUTS } from '@/graphql/payout.graphql'
import moment from 'moment'

export default {
  mixins: [OrganizationLocalization],
  name: 'organizer-payouts-container',
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      payouts: [],
      loading: false,
      error: null,
      downloadingId: null,
    }
  },
  watch: {
    organizationSlug() {
      this.fetchPayouts()
    },
  },
  created() {
    this.fetchPayouts()
  },
  methods: {
    async fetchPayouts() {
      this.loading = true
      this.error = null
      try {
        const { data } = await this.$apollo.query({
          query: GET_ORGANIZER_PAYOUTS,
          variables: { organizationSlug: this.organizationSlug },
          fetchPolicy: 'network-only',
        })
        this.payouts = (data && data.getOrganizerPayouts) || []
      } catch (err) {
        const message =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          'Failed to load payouts'
        this.error = message
      } finally {
        this.loading = false
      }
    },
    async downloadPayout(payout) {
      if (this.downloadingId) {
        return
      }
      const templateId = this.getTemplateId(payout)
      if (!payout.eventId && !templateId) {
        this.$notify && this.$notify({ type: 'error', text: this.organizationText('Missing payout identifiers') })

        return
      }
      this.downloadingId = payout.id
      const from = payout.periodFrom ? payout.periodFrom.toString().slice(0, 10) : undefined
      const to = payout.periodTo ? payout.periodTo.toString().slice(0, 10) : undefined

      try {
        let response
        if (templateId) {
          response = await downloadEventTemplatePayoutDocuments(templateId, {
            from,
            to,
            createInvoice: false,
          })
        } else {
          response = await downloadEventPayoutDocuments(payout.eventId, {
            from,
            to,
            createInvoice: false,
          })
        }

        const blob = new Blob([response.data], { type: 'application/zip' })
        const disposition = response.headers['content-disposition']
        let filename = 'payout-documents.zip'
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/)
          if (match && match[1]) {
            filename = match[1]
          }
        }
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        const message =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          'Failed to download payout'
        this.$notify && this.$notify({ type: 'error', text: this.organizationText(message) })
      } finally {
        this.downloadingId = null
      }
    },
    formatPeriod(from, to, label) {
      if (label) return label
      if (!from && !to) return 'N/A'
      const fromStr = from ? moment(from).format('DD.MM.YYYY') : '…'
      const toStr = to ? moment(to).format('DD.MM.YYYY') : '…'

      return `${fromStr} - ${toStr}`
    },
    formatCurrency(amount) {
      if (amount == null) return '—'
      const value = Number(amount)
      if (Number.isNaN(value)) return amount

      return `${value.toFixed(2)} EUR`
    },
    formatDate(date) {
      if (!date) return '—'

      return moment(date).format('DD.MM.YYYY.')
    },
    getTemplateId(payout) {
      return payout && payout.metadata && payout.metadata.eventTemplateId
        ? payout.metadata.eventTemplateId
        : null
    },
    getEventTitle(payout) {
      if (!payout) return ''
      if (payout.eventTitle) return payout.eventTitle
      if (payout.event && payout.event.title) return payout.event.title

      return payout.eventId ? this.adminText('organizationAll.event_id_e04c3b75', 'Event #{{id}}', { id: payout.eventId }) : this.organizationText('Event')
    },
  },
}
</script>

<style scoped>
.payouts-container {
  padding: 2rem 1rem;
}

.helper-text {
  color: #666;
  margin-top: 0.5rem;
}

.error-message {
  color: #d32f2f;
  margin-top: 1rem;
}

.empty-message {
  margin-top: 1.5rem;
  color: #666;
}

.table-wrapper {
  margin-top: 1.5rem;
  overflow-x: auto;
}

.payouts-table {
  width: 100%;
  border-collapse: collapse;
}

.payouts-table th,
.payouts-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.cell-title {
  font-weight: 600;
  color: #333;
}

.cell-subtitle {
  color: #777;
  font-size: 12px;
}

.status-pill {
  padding: 0.15rem 0.5rem;
  border-radius: 12px;
  text-transform: capitalize;
  font-size: 12px;
  background-color: #f0f0f0;
  color: #555;
}

.status-pill.issued {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.status-pill.pending {
  background-color: #fff3e0;
  color: #f57c00;
}

.status-pill.failed {
  background-color: #ffebee;
  color: #c62828;
}
</style>
